"use client";

import classNames from "classnames";
import { useTranslations } from "next-intl";
import Image from "next/image";
import { useMemo } from "react";

import { ctaClickTracking } from "@/dataLayer/dataLayer";
import useBreakPoint from "@/hooks/useBreakpoint";
import { ImageElement, Media } from "@/types";

import Button, { ButtonSize, ButtonTheme, ButtonVariant } from "../ui/Button/Button";
import Icon from "../ui/Icon/Icon";
import Modal from "../ui/Modal/Modal";
import { HistoryCard, HistoryVariants } from "./types";

type Props = {
  modal: HistoryCard["modal"];
  title: string;
  text: string;
  setModal: Function;
  year?: number;
  image?: ImageElement;
  icon?: string;
  variant: HistoryVariants;
};
export const HistoryModal: React.FC<Props> = ({ modal, setModal, year, image, title, text, icon, variant }) => {
  const t = useTranslations();
  const { isMd } = useBreakPoint("md");
  const topLabel = useMemo(() => {
    return (
      <>
        {!isMd && icon ? (
          <span>
            <Icon name={icon} size="24" className="stroke-black-0" />
          </span>
        ) : (
          <span className="font-primary text-m font-medium">{(variant === HistoryVariants.years && year) || ""}</span>
        )}
        {icon && isMd && (
          <div className="flex justify-center">
            <Icon className="stroke-black-0" name={icon} size="24" />
          </div>
        )}
      </>
    );
  }, [isMd]);

  return (
    <Modal open={!!modal} onClose={() => setModal(undefined)} className="w-100 modal regular-modal history-modal">
      <>
        <div className="modal__content bg-white md:rounded-2xl md:max-h-[643px] flex flex-col h-screen">
          <span className="modal__top-label flex justify-between items-center p-l">
            {topLabel}
            <Button
              className="modal__close"
              size={ButtonSize.MEDIUM}
              variant={ButtonVariant.TERTIARY}
              theme={ButtonTheme.BLACK}
              onClick={() => {
                setModal(undefined);
                ctaClickTracking(t("button.label.close"), "HistoryModal", "HistoryModal");
              }}
              tabIndex={0}
            >
              <span className="pointer-events-none">
                <Icon name="close" size="24" title="close" className="stroke-black" />
                <span className="sr-only">{t("button.label.close")}</span>
              </span>
            </Button>
          </span>
          <div className={classNames("modal__content-wrapper overflow-y-auto pb-l px-l")}>
            {modal && (
              <div className="w-100 md:max-w-[630px]">
                {image && (
                  <Image
                    src={image.url}
                    width={600}
                    height={307}
                    alt={image.title || ""}
                    className="mx-auto w-full h-[307px] object-cover"
                    objectFit="cover"
                  />
                )}
                <p className="font-primary uppercase my-l text-s font-medium">{title}</p>
                <p className="font-secondary-light text-s">{text}</p>
              </div>
            )}
          </div>
        </div>
      </>
    </Modal>
  );
};
